import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Divider, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSecureAuth } from '../../../contexts/SecureAuthContext';
import {
  differentAccountMessage,
  signInAgainWithGoogle,
  signInAgainWithPassword,
} from '../../../services/chat/chatReauth';
import type { SignInAgainResult } from '../../../services/chat/chatReauth';

export interface FreshSignInProps {
  /** The account that opened the flow; a sign-in as any other uid aborts it. */
  uid: string;
  email: string;
  intro?: string;
  onDone: () => void;
  onCancel?: () => void;
  /** Called before the app signs out after a re-login as a different account (discard key material here). */
  onAbort?: () => void;
}

/**
 * The fresh sign-in step (identity reset, settings gate): the full login again
 * with email/password or Google. A sign-in as a different account never
 * continues — it signs that account out, returns to the login screen, and
 * explains which account to use.
 */
export function FreshSignIn({ uid, email, intro, onDone, onCancel, onAbort }: FreshSignInProps) {
  const { logout } = useSecureAuth();
  const navigate = useNavigate();
  const [emailInput, setEmailInput] = useState(email);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const finish = async (result: SignInAgainResult) => {
    if (result.status === 'ok') {
      onDone();
      return;
    }
    onAbort?.();
    await logout();
    navigate('/researcher/auth');
    toast.error(differentAccountMessage(email), { duration: 10_000 });
  };

  const run = async (attempt: () => Promise<SignInAgainResult>) => {
    setBusy(true);
    setError(null);
    try {
      await finish(await attempt());
    } catch (err: any) {
      setError(err?.message || 'Sign-in failed. Please try again.');
      setBusy(false);
    }
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        if (!busy && emailInput && password) {
          void run(() => signInAgainWithPassword(uid, emailInput, password));
        }
      }}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Typography variant="body2">{intro ?? 'Sign in again to continue.'}</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      <TextField
        label="Email Address"
        type="email"
        value={emailInput}
        onChange={(event) => setEmailInput(event.target.value)}
        size="small"
        fullWidth
        disabled={busy}
        autoComplete="username"
      />
      <TextField
        label="Password"
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        size="small"
        fullWidth
        disabled={busy}
        autoComplete="current-password"
        autoFocus
      />
      <Button type="submit" variant="contained" disabled={busy || !emailInput || !password} sx={{ minHeight: 40 }}>
        {busy ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Sign in'}
      </Button>
      <Divider>or</Divider>
      <Button
        variant="outlined"
        disabled={busy}
        onClick={() => void run(() => signInAgainWithGoogle(uid))}
        sx={{ textTransform: 'none' }}
      >
        Continue with Google
      </Button>
      {onCancel && (
        <Button onClick={onCancel} disabled={busy} sx={{ color: 'text.secondary', textTransform: 'none' }}>
          Cancel
        </Button>
      )}
    </Box>
  );
}
